/**
 * MUTAGIM.SHOP — Product Categories Banner
 * Bandeau des catégories affiché en bas des fiches produits
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProductCategoriesBanner() {
  const { i18n, t } = useTranslation();
  const [productCategories, setProductCategories] = useState([]);
  const [brandCategories, setBrandCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const lang = i18n.language?.split('-')[0] || 'fr';

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        if (res.ok) {
          const data = await res.json();
          const allCategories = data.categories || data || [];
          
          // Séparer produits et marques
          setProductCategories(
            allCategories
              .filter(c => c.is_active !== false && c.type !== 'brand')
              .slice(0, 8) // Limiter à 8 catégories produits
          );
          setBrandCategories(
            allCategories
              .filter(c => c.is_active !== false && c.type === 'brand')
              .sort((a, b) => {
                const nameA = (a.name?.fr || a.name || '').toLowerCase();
                const nameB = (b.name?.fr || b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              })
              .slice(0, 12) // Limiter à 12 marques
          );
        }
      } catch (err) {
        console.error('Error fetching categories for banner:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const getName = (name) => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name[lang] || name.fr || name.en || '';
  };

  const getImageUrl = (category) => {
    const img = category.cover_image || category.image_url;
    if (!img) return null;
    if (img.startsWith('http')) return img;
    if (img.startsWith('/api')) return `${API_URL}${img}`;
    return img;
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (productCategories.length === 0 && brandCategories.length === 0) {
    return null;
  }

  return (
    <section className="py-16 border-t border-zinc-800" data-testid="product-categories-banner">
      <div className="max-w-7xl mx-auto px-4">
        {/* Catégories Produits */}
        {productCategories.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📦</span>
                <h2 className="text-xl font-bold text-white">
                  {t('categories.productTypes', 'Explorer par Type')}
                </h2>
              </div>
              <Link 
                to="/categories" 
                className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                data-testid="view-all-product-categories"
              >
                {t('common.viewAll', 'Voir tout')}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {productCategories.map((category) => (
                <Link
                  key={category.id || category._id || category.slug}
                  to={`/category/${category.slug}`}
                  className="group relative overflow-hidden rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-mutagim-red transition-all duration-300"
                  data-testid={`banner-product-category-${category.slug}`}
                >
                  <div className="aspect-[3/2] overflow-hidden bg-zinc-800">
                    {getImageUrl(category) ? (
                      <img
                        src={getImageUrl(category)}
                        alt={getName(category.name)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                        <span className="text-4xl">{category.emoji || '📦'}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="flex items-center gap-2">
                      {category.emoji && <span className="text-lg">{category.emoji}</span>}
                      <h3 className="text-sm font-semibold text-white group-hover:text-mutagim-red transition-colors truncate">
                        {getName(category.name)}
                      </h3>
                    </div>
                    {category.products_count !== undefined && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {category.products_count} {t('categories.products', 'produits')}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Catégories Marques */}
        {brandCategories.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🏷️</span>
                <h2 className="text-xl font-bold text-white">
                  {t('categories.brands', 'Nos Marques')}
                </h2>
              </div>
              <Link 
                to="/categories" 
                className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                data-testid="view-all-brand-categories"
              >
                {t('common.viewAll', 'Voir tout')}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {brandCategories.map((category) => (
                <Link
                  key={category.id || category._id || category.slug}
                  to={`/category/${category.slug}`}
                  className="group relative overflow-hidden rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-indigo-500 transition-all duration-300"
                  data-testid={`banner-brand-category-${category.slug}`}
                >
                  <div className="aspect-square overflow-hidden bg-zinc-800">
                    {getImageUrl(category) ? (
                      <img
                        src={getImageUrl(category)}
                        alt={getName(category.name)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                        <span className="text-2xl font-bold text-white/20">
                          {getName(category.name)?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 text-center">
                    <h3 className="text-xs font-semibold text-white group-hover:text-indigo-400 transition-colors truncate">
                      {getName(category.name)}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default ProductCategoriesBanner;
